import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getCareerBySlug } from '../config/careerPages';
import { getPillarBySlug } from '../config/pillars';
import { fetchGhostPostBySlug } from '../utils/ghost';
import { FAQ_ITEMS as FAQ_FOR_RECRUITERS } from '../screens/forRecruitersFaqData';

const SITE_URL = 'https://jobskills.work';
const JSON_LD_IDS = [
  'seo-jsonld-home',
  'seo-jsonld-howto',
  'seo-jsonld-pricing',
  'seo-jsonld-mock-interview',
  'seo-jsonld-ai-mock-interview',
  'seo-jsonld-blog',
  'seo-jsonld-blogpost',
  'seo-jsonld-career',
  'seo-jsonld-pillar',
  'seo-jsonld-for-recruiters',
];

const PILLAR_PATHS = ['/interview-prep', '/interview-anxiety', '/ai-interview-prep'];

function upsertMeta(name, content) {
  let tag = document.head.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function upsertOgMeta(property, content) {
  let tag = document.head.querySelector(`meta[property="${property}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('property', property);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function upsertCanonical(href) {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}

function removeKnownJsonLd() {
  JSON_LD_IDS.forEach((id) => {
    const existing = document.getElementById(id);
    if (existing) existing.remove();
  });
}

function upsertJsonLd(id, payload) {
  let script = document.getElementById(id);
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(payload);
}

function getPriceValidUntil() {
  const date = new Date();
  date.setFullYear(date.getFullYear() + 1);
  return date.toISOString().slice(0, 10);
}

export default function SeoManager() {
  const location = useLocation();

  useEffect(() => {
    const { pathname, search } = location;
    const currentUrl = `${SITE_URL}${pathname}${search}`;
    const canonicalBase = `${SITE_URL}${pathname}`;

    let title = 'JobSkills — AI Mock Interview Practice & Job Search';
    let description =
      'Practice job interviews with AI. Get BARS-scored feedback on your answers and find jobs that match your skills. Used by 50,000+ job seekers.';
    let robots = 'index, follow';
    let canonical = canonicalBase;
    let jsonLd = null;
    let jsonLdId = null;

    // ── Home ──────────────────────────────────────────────────────────────
    if (pathname === '/') {
      title = 'AI Mock Interview Practice — Get Hired Faster | JobSkills';
      description =
        'AI asks real interview questions. You answer. Get instant BARS-scored feedback — like having a personal interview coach. 3 free sessions, no credit card.';
      jsonLdId = 'seo-jsonld-home';
      jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'JobSkills',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        url: SITE_URL,
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
          description: 'Free plan — 3 mock interview sessions included',
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.8',
          ratingCount: '120',
        },
        description:
          'AI-powered mock interview simulator with BARS-scored feedback. Practice behavioral, situational, and role-specific interview questions.',
      };

    // ── /mock-interview-practice ──────────────────────────────────────────
    } else if (pathname === '/mock-interview-practice') {
      title = 'Free Mock Interview Practice with AI — JobSkills';
      description =
        'Practice job interviews online with an AI interviewer. Get instant BARS-scored feedback on every answer. 3 free sessions — no credit card needed.';
      jsonLdId = 'seo-jsonld-mock-interview';
      jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'JobSkills — Mock Interview Practice',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        url: `${SITE_URL}/mock-interview-practice`,
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
          description: '3 free mock interview sessions included',
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.8',
          ratingCount: '120',
        },
        description:
          'Free AI mock interview practice with instant BARS-scored feedback. Covers behavioral, situational, and role-specific questions for any job.',
      };

    // ── /ai-mock-interview ────────────────────────────────────────────────
    } else if (pathname === '/ai-mock-interview') {
      title = 'AI Mock Interview Simulator — Practice & Get Hired | JobSkills';
      description =
        'The AI interviewer that gives you real feedback, not just encouragement. Paste a job description, answer questions, get BARS scores. Used by 50,000+ job seekers.';
      jsonLdId = 'seo-jsonld-ai-mock-interview';
      jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'JobSkills AI Mock Interview Simulator',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        url: `${SITE_URL}/ai-mock-interview`,
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.8',
          ratingCount: '120',
        },
        description:
          'AI mock interview simulator with BARS-scored feedback. Practice behavioral and role-specific interview questions with an AI that evaluates like a real recruiter.',
      };

    // ── /how-it-works ─────────────────────────────────────────────────────
    } else if (pathname === '/how-it-works') {
      title = 'How to Practice Job Interviews with AI — Step-by-Step | JobSkills';
      description =
        'Learn how JobSkills AI interview practice works in 4 steps: paste a job posting, answer questions, get BARS feedback, track your progress and get hired.';
      jsonLdId = 'seo-jsonld-howto';
      jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: 'How to Practice Job Interviews with AI Using JobSkills',
        description:
          'A step-by-step guide to practicing job interviews with an AI simulator and getting structured BARS-scored feedback.',
        step: [
          {
            '@type': 'HowToStep',
            name: 'Paste the job description',
            text: 'Find a role you want to apply for and paste the job posting. JobSkills reads it and generates interview questions tailored to that exact position.',
          },
          {
            '@type': 'HowToStep',
            name: 'Answer like in a real interview',
            text: 'Type or speak your answers. The AI interviewer asks follow-up questions and keeps you on track.',
          },
          {
            '@type': 'HowToStep',
            name: 'Get BARS-scored feedback',
            text: 'Each answer is scored using the Behaviorally Anchored Rating Scale — the same framework real recruiters use at top companies.',
          },
          {
            '@type': 'HowToStep',
            name: 'Track your progress',
            text: 'See your scores improve across sessions. Know exactly which competencies to work on before your real interview.',
          },
        ],
      };

    // ── /pricing ──────────────────────────────────────────────────────────
    } else if (pathname === '/pricing') {
      title = 'JobSkills Pricing — Free & Pro AI Interview Practice Plans';
      description =
        'Start free with 3 mock interview sessions. Upgrade to Pro for unlimited practice, role-specific questions, and full progress tracking. No commitment needed.';
      jsonLdId = 'seo-jsonld-pricing';
      const priceValidUntil = getPriceValidUntil();
      jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: 'JobSkills Subscription',
        description:
          'AI mock interview practice subscription. Choose from Free, Starter, or Pro plans.',
        url: `${SITE_URL}/pricing`,
        offers: [
          {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'USD',
            priceValidUntil,
            name: 'Free',
            description: '3 mock interview sessions with BARS feedback',
          },
          {
            '@type': 'Offer',
            price: '19.99',
            priceCurrency: 'USD',
            priceValidUntil,
            name: 'Starter Monthly',
          },
          {
            '@type': 'Offer',
            price: '149',
            priceCurrency: 'USD',
            priceValidUntil,
            name: 'Starter Yearly',
          },
          {
            '@type': 'Offer',
            price: '39.99',
            priceCurrency: 'USD',
            priceValidUntil,
            name: 'Pro Monthly',
            description: 'Unlimited mock sessions, full question library, progress dashboard',
          },
          {
            '@type': 'Offer',
            price: '299',
            priceCurrency: 'USD',
            priceValidUntil,
            name: 'Pro Yearly',
          },
        ],
      };

    // ── Pillar pages: /interview-prep, /interview-anxiety, /ai-interview-prep
    } else if (PILLAR_PATHS.includes(pathname)) {
      const pillarSlug = pathname.slice(1);
      const pillar = getPillarBySlug(pillarSlug);
      if (pillar) {
        title = pillar.metaTitle || pillar.title;
        description = pillar.metaDescription || pillar.intro?.slice(0, 160) || description;
        canonical = `${SITE_URL}${pathname}`;
        jsonLdId = 'seo-jsonld-pillar';
        jsonLd = {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: pillar.h1 || pillar.title,
          description,
          url: canonical,
          datePublished: '2026-04-29',
          dateModified: new Date().toISOString().slice(0, 10),
          author: { '@type': 'Organization', name: 'JobSkills' },
          publisher: {
            '@type': 'Organization',
            name: 'JobSkills',
            logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo.png` },
          },
          mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
          ...(pillar.faq?.length > 0 && {
            mainEntity: {
              '@type': 'FAQPage',
              mainEntity: pillar.faq.map((item) => ({
                '@type': 'Question',
                name: item.q,
                acceptedAnswer: { '@type': 'Answer', text: item.a },
              })),
            },
          }),
        };
      }

    // ── /blog/:slug ───────────────────────────────────────────────────────
    } else if (pathname.startsWith('/blog/') && pathname.split('/').filter(Boolean).length === 2) {
      const blogSlug = pathname.split('/').filter(Boolean)[1];
      // Initial placeholder values; overridden async after Ghost fetch below.
      title = 'Loading article… | JobSkills';
      description = 'Loading interview-prep article from the JobSkills blog.';
      canonical = `${SITE_URL}/blog/${blogSlug}`;

    // ── /blog ─────────────────────────────────────────────────────────────
    } else if (pathname === '/blog') {
      title = 'Interview Preparation Blog — Tips, Guides & Resources | JobSkills';
      description =
        'Expert advice on job interviews, the STAR method, behavioral questions, and career development. Practical guides updated weekly for active job seekers.';
      jsonLdId = 'seo-jsonld-blog';
      jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'Blog',
        name: 'JobSkills Interview Preparation Blog',
        description:
          'Expert interview preparation guides, STAR method examples, behavioral question breakdowns, and career tips for job seekers.',
        url: `${SITE_URL}/blog`,
        publisher: {
          '@type': 'Organization',
          name: 'JobSkills',
          url: SITE_URL,
        },
      };

    // ── /career/:slug and /role/:slug ─────────────────────────────────────
    } else if (pathname.startsWith('/career/') || pathname.startsWith('/role/')) {
      const slug = pathname.split('/').filter(Boolean)[1] || '';
      const career = getCareerBySlug(slug);
      if (career) {
        title = `AI Interview Prep for ${career.title} — Practice & Get Hired | JobSkills`;
        description =
          career.description ||
          `Practice mock interviews for ${career.title} positions. Get instant BARS-scored AI feedback on behavioral, technical, and situational questions. Start free.`;
        jsonLdId = 'seo-jsonld-career';
        jsonLd = {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: `${career.title} Interview Preparation | JobSkills`,
          description: description,
          url: `${SITE_URL}/career/${slug}`,
          ...(career.faq && career.faq.length > 0 && {
            mainEntity: {
              '@type': 'FAQPage',
              mainEntity: career.faq.map((item) => ({
                '@type': 'Question',
                name: item.q,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: item.a,
                },
              })),
            },
          }),
        };
      } else {
        title = 'Career Interview Prep | JobSkills';
        description = 'Practice AI mock interviews for any career role. Get BARS-scored feedback and find top jobs.';
      }
      robots = 'index, follow';
      canonical = `${SITE_URL}/career/${slug}`;

    // ── /for-recruiters (public B2B marketing landing) ────────────────────
    } else if (pathname === '/for-recruiters') {
      title = 'Async Candidate Screening Tool for Recruiters & Agencies | Jobskills';
      description =
        'Replace 30-minute screening calls with structured async questionnaires. Send candidates a link, get comparable answers in one dashboard. Free for 10 screenings.';
      canonical = `${SITE_URL}/for-recruiters`;
      jsonLdId = 'seo-jsonld-for-recruiters';
      jsonLd = {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'SoftwareApplication',
            '@id': `${SITE_URL}/for-recruiters#software`,
            name: 'Jobskills for Recruiters',
            applicationCategory: 'BusinessApplication',
            applicationSubCategory: 'Recruiting Software',
            operatingSystem: 'Web',
            description:
              'Async candidate screening tool for recruiting agencies and in-house recruiters. Replaces 30-minute screening calls with 15-minute structured questionnaires.',
            url: `${SITE_URL}/for-recruiters`,
            offers: [
              { '@type': 'Offer', name: 'Free', price: '0', priceCurrency: 'USD' },
              { '@type': 'Offer', name: 'Starter', price: '39', priceCurrency: 'USD' },
              { '@type': 'Offer', name: 'Agency', price: '149', priceCurrency: 'USD' },
              { '@type': 'Offer', name: 'Scale', price: '349', priceCurrency: 'USD' },
            ],
            provider: {
              '@type': 'Organization',
              name: 'Jobskills',
              url: SITE_URL,
            },
          },
          {
            '@type': 'BreadcrumbList',
            '@id': `${SITE_URL}/for-recruiters#breadcrumb`,
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'For Recruiters',
                item: `${SITE_URL}/for-recruiters`,
              },
            ],
          },
          {
            '@type': 'FAQPage',
            '@id': `${SITE_URL}/for-recruiters#faq`,
            mainEntity: FAQ_FOR_RECRUITERS.map((item) => ({
              '@type': 'Question',
              name: item.q,
              acceptedAnswer: { '@type': 'Answer', text: item.a },
            })),
          },
        ],
      };

    // ── /contacts ─────────────────────────────────────────────────────────
    } else if (pathname === '/contacts') {
      title = 'Contact JobSkills — AI Interview Practice Platform';
      description = 'Get in touch with the JobSkills team. Questions about plans, features, or billing? We respond within 24 hours.';

    // ── /terms ────────────────────────────────────────────────────────────
    } else if (pathname === '/terms') {
      title = 'Terms of Service | JobSkills';
      description = 'JobSkills terms of service, privacy policy, and user agreement.';
      robots = 'noindex, follow';

    // ── /refund-policy ────────────────────────────────────────────────────
    } else if (pathname === '/refund-policy') {
      title = 'Refund Policy | JobSkills';
      description = 'JobSkills refund and cancellation policy for Pro and Starter subscription plans.';
      robots = 'noindex, follow';

    // ── /search and /results ──────────────────────────────────────────────
    } else if (pathname.startsWith('/search') || pathname.startsWith('/results')) {
      title = 'Job Search | JobSkills';
      description = 'Search jobs and prepare for interviews with AI.';
      robots = 'noindex, follow';
      canonical = `${SITE_URL}/search`;

    // ── Private pages ─────────────────────────────────────────────────────
    } else if (
      pathname.startsWith('/profile') ||
      pathname.startsWith('/chat') ||
      pathname.startsWith('/chats') ||
      pathname.startsWith('/interview')
    ) {
      robots = 'noindex, nofollow';
      canonical = canonicalBase;

    // ── B2B kabinet (/b2b/*), candidate screening (/screen/:token), and the
    // public lead-gen quiz (/quiz/:slug) ───────────────────────────────────
    // /b2b/auth — public-funnel landing; всё остальное /b2b/* — приватный кабинет.
    // /screen/:token — public кандидатская страница, но содержимое уникально-личное → noindex.
    // /quiz/:slug — outreach-only funnel, not for organic search → noindex.
    } else if (
      pathname.startsWith('/b2b/') ||
      pathname.startsWith('/screen/') ||
      pathname.startsWith('/quiz/')
    ) {
      robots = 'noindex, nofollow';
      canonical = canonicalBase;
      // B2B, screening, и quiz pages не должны попадать в schema.org/JSON-LD блог/HowTo discovery.
    }

    document.title = title;
    upsertMeta('description', description);
    upsertMeta('robots', robots);
    upsertCanonical(canonical);

    // Open Graph tags
    upsertOgMeta('og:title', title);
    upsertOgMeta('og:description', description);
    upsertOgMeta('og:url', currentUrl);
    upsertOgMeta('og:type', 'website');
    upsertOgMeta('og:site_name', 'JobSkills');
    upsertOgMeta('og:image', `${SITE_URL}/logo.png`);

    // Twitter Card
    upsertMeta('twitter:card', 'summary_large_image');
    upsertMeta('twitter:title', title);
    upsertMeta('twitter:description', description);
    upsertMeta('twitter:image', `${SITE_URL}/logo.png`);

    removeKnownJsonLd();
    if (jsonLd && jsonLdId) upsertJsonLd(jsonLdId, jsonLd);

    // Async meta override for /blog/:slug — fetch post from Ghost and
    // replace title/description/og/JSON-LD with article-specific values.
    let aborted = false;
    const blogSlugMatch = pathname.match(/^\/blog\/([^/]+)\/?$/);
    if (blogSlugMatch) {
      const blogSlug = blogSlugMatch[1];
      fetchGhostPostBySlug(blogSlug)
        .then((post) => {
          if (aborted || !post) return;
          const articleTitle = post.meta_title || post.title;
          const articleDesc =
            post.meta_description ||
            post.custom_excerpt ||
            (post.excerpt ? post.excerpt.slice(0, 160) : description);
          const articleImage = post.og_image || post.feature_image || `${SITE_URL}/logo.png`;
          const articleCanonical = post.canonical_url || `${SITE_URL}/blog/${blogSlug}`;
          const articleUrl = `${SITE_URL}/blog/${blogSlug}`;

          document.title = articleTitle;
          upsertMeta('description', articleDesc);
          upsertCanonical(articleCanonical);

          upsertOgMeta('og:title', post.og_title || articleTitle);
          upsertOgMeta('og:description', post.og_description || articleDesc);
          upsertOgMeta('og:url', articleUrl);
          upsertOgMeta('og:type', 'article');
          upsertOgMeta('og:image', articleImage);

          upsertMeta('twitter:title', post.twitter_title || articleTitle);
          upsertMeta('twitter:description', post.twitter_description || articleDesc);
          upsertMeta('twitter:image', post.twitter_image || articleImage);

          upsertJsonLd('seo-jsonld-blogpost', {
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: post.title,
            description: articleDesc,
            url: articleCanonical,
            image: articleImage,
            datePublished: post.published_at,
            dateModified: post.updated_at || post.published_at,
            author: post.authors?.[0]?.name
              ? { '@type': 'Person', name: post.authors[0].name }
              : { '@type': 'Organization', name: 'JobSkills' },
            publisher: {
              '@type': 'Organization',
              name: 'JobSkills',
              logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo.png` },
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': articleCanonical,
            },
          });
        })
        .catch(() => {
          // Silent — keep placeholder meta if Ghost is unreachable
        });
    }

    return () => {
      aborted = true;
    };
  }, [location]);

  return null;
}
